import { cn } from "@/shared/lib/utils";
import { StatusText } from "@/shared/components/ui/status-chip";
import type { LeadActivity, LeadActivityEvent, LeadDetailContact } from "../types";

/**
 * Contacts & engagement panel for the lead-profile page. Contacts ALWAYS render (from the
 * lead detail — identity, verified/opted-out, role) so the profile shows them even before
 * any outreach; each contact's Smartlead delivery events (from GET /leads/:id/activity) are
 * overlaid as a timeline when present. Repeat opens are shown as a count (there is no
 * distinct REOPEN event — a re-open is just another OPEN).
 */

type Tone = "accent" | "value" | "warning" | "danger" | "muted" | "faint";
/** Tones accepted by StatusText (no "muted"). */
type StatusTone = "accent" | "value" | "warning" | "danger" | "faint";

const EVENT_TONE: Record<string, Tone> = {
  SENT: "muted",
  OPEN: "accent",
  CLICK: "accent",
  REPLY: "accent",
  BOUNCE: "warning",
  UNSUBSCRIBE: "danger",
};

const CONTACT_STATUS_TONE: Record<string, StatusTone> = {
  ACTIVE: "value",
  REPLIED: "accent",
  BOUNCED: "warning",
  UNSUBSCRIBED: "danger",
};

function fmt(at: string): string {
  const d = new Date(at);
  if (Number.isNaN(d.getTime())) return "";
  return d.toLocaleString(undefined, { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" });
}

function toneClass(t: Tone): string {
  return {
    accent: "text-ge-accent",
    value: "text-ge-value",
    warning: "text-ge-warning",
    danger: "text-ge-danger",
    muted: "text-ge-muted",
    faint: "text-ge-faint",
  }[t];
}

export function ContactsPanel({
  contacts,
  activity,
  activityLoading,
  activityError,
}: {
  contacts: LeadDetailContact[];
  activity: LeadActivity | undefined;
  activityLoading: boolean;
  activityError: boolean;
}) {
  if (contacts.length === 0) {
    return (
      <p className="text-[12px] text-ge-muted" data-testid="contacts-empty">
        No contacts found yet. Run enrichment on this lead to find contact emails.
      </p>
    );
  }
  const eventsByContact = new Map<string, LeadActivityEvent[]>(
    (activity?.contacts ?? []).map((c) => [c.contactId, c.events]),
  );
  const hasEvents = (activity?.contacts ?? []).some((c) => c.events.length > 0);

  return (
    <div className="space-y-4" data-testid="contacts-panel">
      {activity && hasEvents && <TotalsStrip totals={activity.totals} />}
      {activityError && (
        <p className="text-[11.5px] text-ge-danger">Couldn&apos;t load engagement — showing contacts only.</p>
      )}
      <div className="space-y-3">
        {contacts.map((c) => (
          <ContactCard
            key={c.id}
            contact={c}
            events={eventsByContact.get(c.id) ?? []}
            loading={activityLoading}
          />
        ))}
      </div>
    </div>
  );
}

function TotalsStrip({ totals }: { totals: LeadActivity["totals"] }) {
  const cells: { label: string; value: number; tone: Tone }[] = [
    { label: "Sent", value: totals.sent, tone: "value" },
    { label: "Opened", value: totals.opened, tone: "accent" },
    { label: "Opens", value: totals.opensTotal, tone: "faint" },
    { label: "Clicked", value: totals.clicked, tone: "accent" },
    { label: "Replied", value: totals.replied, tone: "accent" },
    { label: "Bounced", value: totals.bounced, tone: "warning" },
    { label: "Unsub", value: totals.unsubscribed, tone: "danger" },
  ];
  return (
    <div className="flex flex-wrap gap-x-5 gap-y-1" data-testid="engagement-totals">
      {cells.map((c) => (
        <div key={c.label} className="flex items-baseline gap-1.5">
          <span className={cn("font-mono text-[13px] font-semibold", toneClass(c.tone))}>{c.value}</span>
          <span className="text-[11px] text-ge-muted">{c.label}</span>
        </div>
      ))}
    </div>
  );
}

function ContactCard({
  contact,
  events,
  loading,
}: {
  contact: LeadDetailContact;
  events: LeadActivityEvent[];
  loading: boolean;
}) {
  const opens = events.filter((e) => e.type === "OPEN").length;
  return (
    <div className="rounded-ge-param border border-ge-border-card p-3" data-testid={`contact-${contact.id}`}>
      <div className="flex items-center justify-between gap-2">
        <span className="min-w-0 truncate font-mono text-[12px] text-ge-primary">{contact.email ?? "—"}</span>
        <span className="flex shrink-0 items-center gap-2 font-mono text-[10.5px]">
          {contact.verified && <span className="text-ge-accent">✓ verified</span>}
          {opens > 1 && <span className="text-ge-accent">opened {opens}×</span>}
          {contact.optedOut && <span className="text-ge-danger">opted out</span>}
          <StatusText tone={CONTACT_STATUS_TONE[contact.status] ?? "value"}>{contact.status}</StatusText>
        </span>
      </div>
      {(contact.role || contact.source) && (
        <p className="mt-1 font-mono text-[10.5px] text-ge-muted">
          {[contact.role, contact.source].filter(Boolean).join(" · ")}
        </p>
      )}
      {loading ? (
        <p className="mt-2 font-mono text-[10.5px] text-ge-faint">loading activity…</p>
      ) : events.length > 0 ? (
        <ul className="mt-2 space-y-1">
          {events.map((e) => (
            <EventRow key={e.id} e={e} />
          ))}
        </ul>
      ) : (
        <p className="mt-2 font-mono text-[10.5px] text-ge-faint">no activity yet</p>
      )}
    </div>
  );
}

function EventRow({ e }: { e: LeadActivityEvent }) {
  const tone = EVENT_TONE[e.type] ?? "value";
  return (
    <li className="flex items-baseline justify-between gap-3 font-mono text-[11px]">
      <span className="flex items-baseline gap-2">
        <span className={cn("font-semibold", toneClass(tone))}>{e.type}</span>
        {e.stepOrder != null && <span className="text-ge-faint">step {e.stepOrder}</span>}
        {e.sequenceName && <span className="truncate text-ge-muted">· {e.sequenceName}</span>}
      </span>
      <span className="shrink-0 text-ge-faint">{fmt(e.at)}</span>
    </li>
  );
}
